import { IWardConfiguration } from "../@types/Login";
import { AUTH_WARD_CONFIGURATION } from "../constants/localStorageKeys";
import { safeJsonParse, safeStorage } from "./storage";

/**
 * EN-4960: a patient can belong to multiple wards. Ward feature flags are unioned across the
 * patient's wards — a capability is available when ANY of the patient's wards enables it. These
 * helpers centralise that rule (replacing the old single-ward `find(wc.hospitalId === hospitalId)`).
 */

function readWardConfigurations(): IWardConfiguration[] {
  const stored =
    safeJsonParse<IWardConfiguration[] | undefined>(
      safeStorage.getItem(AUTH_WARD_CONFIGURATION),
      undefined
    ) ?? [];

  // Read-time shim for pre-EN-4960 stored shapes: sessions authenticated before this deploy
  // hold `{ hospitalId, hospitalName, ...flags }`. Map those forward to `{ wardId, wardName }`
  // so ward flags keep resolving until the user re-logs-in (the blob is only rewritten at login).
  return stored.map((entry) => {
    const legacy = entry as IWardConfiguration & {
      hospitalId?: number;
      hospitalName?: string;
    };
    return {
      ...entry,
      wardId: legacy.wardId ?? legacy.hospitalId,
      wardName: legacy.wardName ?? legacy.hospitalName,
    };
  });
}

/** The ward configurations (from login) for the wards the patient belongs to. */
export function getWardConfigsForPatient(
  patientWardIds: number[]
): IWardConfiguration[] {
  if (!patientWardIds.length) return [];
  return readWardConfigurations().filter((wc) =>
    patientWardIds.includes(wc.wardId)
  );
}

/**
 * Resolves the ward ids to use for a patient: the full set when available, otherwise the primary
 * ward (cms_patient.hospital_id) as a single-element fallback for graceful degradation.
 */
export function resolvePatientWardIds(
  wardIds: number[] | undefined,
  primaryHospitalId?: string | number | null
): number[] {
  if (wardIds && wardIds.length) return wardIds;
  if (
    primaryHospitalId === undefined ||
    primaryHospitalId === null ||
    primaryHospitalId === ""
  ) {
    return [];
  }
  const primary =
    typeof primaryHospitalId === "string"
      ? parseInt(primaryHospitalId, 10)
      : primaryHospitalId;
  return Number.isNaN(primary) ? [] : [primary];
}

/** True when ANY of the patient's wards enables the given boolean feature flag. */
export function isWardFeatureEnabledForPatient(
  patientWardIds: number[],
  flag: keyof IWardConfiguration
): boolean {
  return getWardConfigsForPatient(patientWardIds).some((wc) => !!wc[flag]);
}

/**
 * EN-3346: True when ANY of the patient's wards is on the "NewApp" onboarding
 * strategy. Gates the Protocol actions table on the patient page.
 */
export function isNewAppOnboardingForPatient(
  patientWardIds: number[]
): boolean {
  return getWardConfigsForPatient(patientWardIds).some(
    (wc) => wc.onboardingStrategy === "NewApp"
  );
}
